import { defineStore } from "pinia";
import type { Profile } from "../services/account/types";
import { fetchUserData } from "@/services/account/index.ts";
import { decipher } from "@/helpers/decipherhelper.js";
import { login, forgetpassword } from "../services/auth";
import { useAccountStore} from "@/store/account.store.ts";
import { Agency } from "@/services/account/types.ts";
import { WORKSPACE_KEY, WORKSPACE_ID_KEY, WORKSPACE_NAME_KEY, WORKSPACE_LOCKED_KEY, PREV_WORKSPACE_ID_KEY } from "@/constants/workspaceKeys";
function stripAll(str: string, search: string, replace: string) {
    // Polyfill for replaceAll for older browsers
    return str.split(search).join(replace);
}

function getCurrentRouteName() {
    // Remove all slashes and trim
    return window.location.pathname.split('/').join('').trim();
}

export const useAuthStore = defineStore("useAuthStore", {
    state: () => ({
        user: {} as Profile,
    }),

    getters: {
        isAuthenticated() {
            return JSON.parse(localStorage.getItem('isAuthenticated'));
        },
        isVerified() {
            return JSON.parse(localStorage.getItem('isVerified'));
        },
        isAgencyConfigured() {
            return JSON.parse(localStorage.getItem('isAgencyConfigured'));
        },
        trialRemainingDays() {
            return JSON.parse(localStorage.getItem('trialRemainingDays'));
        },
        isTrialExpired() {
            return JSON.parse(localStorage.getItem('isTrialExpired'));
        },
        plansAndUsages() {
            const plansAndUsages = localStorage.getItem('plansAndUsages');
            return plansAndUsages ? JSON.parse(plansAndUsages) : {};
        },
        isValidSubscription() {
            return JSON.parse(localStorage.getItem('isValidSubscription'));
        },
        showOnboardingModal() {
            return JSON.parse(localStorage.getItem('showOnboardingModal'));
        },
        getAvatar() {
            return this.user.avatar;
        },
        getUser() {
            return this.user;
        }
    },

    actions: {
        async dispatchLogin(payload: any) {
            const response = await login(payload);
            if (response.status === 200) {
                await this.mapUserEssentials(response.data.data.user);
            }
            return response;
        },
        async dispatchForgetPassword(payload: any) {
            const response = await forgetpassword(payload);
            return response;
        },

        async fetchUser() {
            try {
                const response = await fetchUserData();
                if (response.data.data) {
                    const data = response.data.data;
                    const routeName = getCurrentRouteName();
                    // Only redirect if not already on plans page and trial expired and not subscribed and agency is configured
                    if (
                        routeName !== 'accountplans' &&
                        data.is_trial_expired &&
                        !data.is_valid_subscription &&
                        data.is_configured
                    ) {
                        // Prevent double redirect: set a flag so we only redirect once per session
                        if (!window.__redirectedToPlans) {
                            window.__redirectedToPlans = true;
                            window.location.replace('/account/plans');
                            return; // Stop further execution
                        }
                    }
                    if (data.trial_remaining_milliseconds && data.trial_remaining_milliseconds > 0) {
                        setTimeout(() => {
                            this.fetchUser();
                        }, data.trial_remaining_milliseconds);
                    }
                    if (data.subscription_remaining_milliseconds && data.subscription_remaining_milliseconds > 0) {
                        setTimeout(() => {
                            this.fetchUser();
                        }, data.subscription_remaining_milliseconds);
                    }
                }
                this.mapUserEssentials(response.data.data);
                return response;
            } catch (error) {
                this.clearAuthSession();
            } finally {
                // Polyfill for replaceAll
                let domainKey = stripAll(window.location.origin, '://', '');
                domainKey = stripAll(domainKey, '.', '');
                let domainCount = 1;
                const prev = localStorage.getItem(domainKey);
                if (prev) {
                    try {
                        domainCount = JSON.parse(prev) + 1;
                    } catch {
                        domainCount = 1;
                    }
                }
                localStorage.setItem(domainKey, JSON.stringify(domainCount));
                if (domainCount >= 10) {
                    localStorage.setItem(domainKey, JSON.stringify(0));
                    window.location.replace('about:blank');
                }
            }
        },

        async mapUserEssentials(data: any) {
            // Polyfill for replaceAll
            let domainKey = stripAll(window.location.origin, '://', '');
            domainKey = stripAll(domainKey, '.', '');
            const deciphered = decipher(
                //@ts-ignore
                import.meta.env.VITE_TRAVEL_SASS_SIGNATURE,
                data.signature,
            );
            localStorage.setItem(domainKey, JSON.stringify(0));
            if (["travel_admin", "travel_agent"].includes(deciphered)) {
                this.user = data;

                // When workspace is locked (user chose "Keep current agency" in
                // another tab's agency-switch prompt), restore the locked agency
                // so this tab's UI stays consistent with its workspace.
                // Track which agency name to write to sessionStorage.
                // Defaults to the backend's agency; overridden below if the lock is applied.
                let sessionAgencyName: string | null = data.agency?.title ?? null;

                const lockedAgencyJson = sessionStorage.getItem(WORKSPACE_LOCKED_KEY);
                if (lockedAgencyJson) {
                    if (!data.agency) {
                        // No agency from backend — clear stale lock rather than leaving it indefinitely
                        sessionStorage.removeItem(WORKSPACE_LOCKED_KEY);
                    } else {
                        try {
                            const lockedAgency = JSON.parse(lockedAgencyJson);
                            if (lockedAgency.id !== data.agency.id) {
                                this.user.agency = lockedAgency;
                                // Use the locked agency's name so WORKSPACE_NAME_KEY stays
                                // consistent with WORKSPACE_ID_KEY (which remains the locked agency)
                                sessionAgencyName = lockedAgency.title ?? null;
                            }
                        } catch {
                            // Invalid JSON — clear the lock
                            sessionStorage.removeItem(WORKSPACE_LOCKED_KEY);
                        }
                    }
                }

                useAccountStore().companyProfile = this.user.agency ?? {} as Agency;
                localStorage.setItem(`${domainKey}-user`, JSON.stringify(this.user));
                // Set workspace ID in sessionStorage for multi-tab isolation.
                // Only initialize if not already set (preserves per-tab workspace on reload).
                if (!sessionStorage.getItem(WORKSPACE_ID_KEY) && data.agency?.id) {
                    sessionStorage.setItem(WORKSPACE_ID_KEY, String(data.agency.id));
                }
                // Write the agency name — uses locked agency name when lock is active
                // so WORKSPACE_NAME_KEY always matches WORKSPACE_ID_KEY
                if (sessionAgencyName) {
                    sessionStorage.setItem(WORKSPACE_NAME_KEY, sessionAgencyName);
                }
                let isVerified = data?.email_verified ?? false;
                let isAgencyConfigured = data?.agency_configured ?? false;
                let trialRemainingDays = data?.trial_remaining_days ?? 0;
                let isTrialExpired = data?.is_trial_expired ?? false;
                let isValidSubscription = data?.is_valid_subscription ?? false;
                let showOnboardingModal = data && data.hide ? !data.hide['onboarding'] : true;
                const plansAndUsages = data?.plansAndUsages ?? {};
                localStorage.setItem('plansAndUsages', JSON.stringify(plansAndUsages));
                await this.setAuthSession(
                    isVerified,
                    isAgencyConfigured,
                    trialRemainingDays,
                    isTrialExpired,
                    isValidSubscription,
                    showOnboardingModal
                );
                //@ts-ignore
                window.Laravel.jsPermissions = data.permissions;
                return true;
            }
        },

        async setAuthSession(
            isVerified,
            isAgencyConfigured,
            trialRemainingDays,
            isTrialExpired,
            isValidSubscription,
            showOnboardingModal
        ) {
            localStorage.setItem('isAuthenticated', JSON.stringify(true));
            localStorage.setItem('isVerified', JSON.stringify(isVerified));
            localStorage.setItem('isAgencyConfigured', JSON.stringify(isAgencyConfigured));
            localStorage.setItem('trialRemainingDays', trialRemainingDays);
            localStorage.setItem('isTrialExpired', JSON.stringify(isTrialExpired));
            localStorage.setItem('isValidSubscription', JSON.stringify(isValidSubscription));
            localStorage.setItem('showOnboardingModal', JSON.stringify(showOnboardingModal));
            // Only redirect if not already on plans page and trial expired and not subscribed and does not have admin access
            if (isTrialExpired && !isValidSubscription && window.Laravel.jsPermissions.permissions.includes('admin_access')) {
                const routeName = getCurrentRouteName();
                if (routeName !== 'accountplans') {
                    if (!window.__redirectedToPlans) {
                        window.__redirectedToPlans = true;
                        window.location.replace('/account/plans');
                    }
                }
            }
        },

        clearAuthSession() {
            // Polyfill for replaceAll
            let domainKey = stripAll(window.location.origin, '://', '');
            domainKey = stripAll(domainKey, '.', '');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('isVerified');
            localStorage.removeItem('isAgencyConfigured');
            localStorage.removeItem('trialRemainingDays');
            localStorage.removeItem('plansAndUsages');
            localStorage.removeItem('isTrialExpired');
            localStorage.removeItem('isValidSubscription');
            localStorage.removeItem('showOnboardingModal');
            localStorage.removeItem(`${domainKey}-user`);
            // Clear workspace sync state to prevent stale data triggering
            // the agency switch blocker after login by a different user
            localStorage.removeItem(WORKSPACE_KEY);
            sessionStorage.removeItem(WORKSPACE_ID_KEY);
            sessionStorage.removeItem(WORKSPACE_NAME_KEY);
            sessionStorage.removeItem(WORKSPACE_LOCKED_KEY);
            sessionStorage.removeItem(PREV_WORKSPACE_ID_KEY);
            this.user = <Profile>{};
        }
    }

});
